import React, { useMemo } from "react";

import { faCaretDown, faCaretUp } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

import { Chip, ICellRendererParams, PYColumnDef, Text } from "@performyard/py-components";

import { SurveyQuestionRow } from "../types";

const EXPANDABLE_TYPES = ["Rating Scale", "Multiple Choice"];

export type SurveyResultsTableContext = {
	onExpandToggle: (rowId: string) => void;
	expandedRowId: string | null;
};

export const getSurveyResultsColumns = (): PYColumnDef<SurveyQuestionRow>[] => [
	{
		colId: "question_type",
		headerName: "Type",
		width: 120,
		field: "question_type",
		sortable: true,
	},
	{
		colId: "question_text",
		headerName: "Question",
		flex: 1,
		field: "question_text",
		sortable: true,
		cellRenderer: (params: ICellRendererParams<SurveyQuestionRow>) => {
			if (!params.data) return null;
			return (
				<div data-testid="question-text-cell" className="flex h-full w-full items-center">
					<Text size="paragraph-3" weight="regular" dangerouslySetClassName="py-line-clamp-1">
						{params.data.question_text}
					</Text>
				</div>
			);
		},
	},
	{
		colId: "top_response",
		headerName: "Top Response",
		sortable: false,
		width: 360,
		cellRenderer: (params: ICellRendererParams<SurveyQuestionRow>) => {
			if (!params.data) return null;
			const context = params.context as SurveyResultsTableContext | undefined;
			const isExpandable =
				!!context &&
				!params.data.superRow &&
				params.data.response_count > 0 &&
				EXPANDABLE_TYPES.includes(params.data.question_type);
			const isExpanded = isExpandable && context!.expandedRowId === params.data._rowId;

			const onToggle = isExpandable ? () => context!.onExpandToggle(params.data!._rowId) : undefined;

			return (
				<TopResponseCell
					optionsResults={params.data.options_results}
					totalResponses={params.data.response_count}
					isExpanded={isExpanded}
					isExpandable={isExpandable}
					onClickText={onToggle}
				/>
			);
		},
	},
	{
		colId: "response_count",
		headerName: "# of Responses",
		width: 114,
		field: "response_count",
		sortable: true,
		cellRenderer: (params: ICellRendererParams<SurveyQuestionRow>) => {
			if (!params.data) return null;
			return <div className="flex w-full justify-center">{params.data.response_count}</div>;
		},
	},
];

const TopResponseCell = ({
	optionsResults,
	totalResponses,
	isExpanded = false,
	isExpandable = false,
	onClickText,
}: {
	optionsResults: SurveyQuestionRow["options_results"];
	totalResponses: number;
	isExpanded?: boolean;
	isExpandable?: boolean;
	onClickText?: () => void;
}) => {
	const [topResponseText, percentage]: [string | null, number | null] = useMemo(() => {
		if (!optionsResults?.length || totalResponses === 0) return [null, null];

		const sortedOptions = [...optionsResults].sort((a, b) => b.response_count - a.response_count);
		const topOption = sortedOptions[0];

		if (!topOption || topOption.response_count === 0) return [null, null];

		const isTied = sortedOptions.length > 1 && topOption.response_count === sortedOptions[1].response_count;

		if (isTied) {
			const tiedCount = sortedOptions.filter(
				(option) => option.response_count === topOption.response_count
			).length;
			return [`Tied Between ${tiedCount} Responses`, (topOption.response_count / totalResponses) * 100];
		}

		const displayText =
			topOption.option_value || topOption.option_value === 0
				? `${topOption.option_value} — ${topOption.option_text}`
				: topOption.option_text;

		return [displayText, (topOption.response_count / totalResponses) * 100];
	}, [optionsResults, totalResponses]);

	return (
		<div className="flex h-full items-center gap-1" data-testid="top-response-cell">
			<div className="flex gap-1 hover:text-primary-5 hover:underline">
				{isExpandable && (
					<div
						data-testid="expand-arrow"
						className="flex shrink-0 cursor-pointer items-center pr-1"
						onClick={onClickText}
					>
						<FontAwesomeIcon
							icon={isExpanded ? faCaretUp : faCaretDown}
							className={isExpanded ? "text-primary-5" : ""}
						/>
					</div>
				)}
				<div className="flex min-w-0 flex-1 items-center gap-1">
					{topResponseText ? (
						<span onClick={onClickText} className={onClickText ? "cursor-pointer" : ""}>
							<Text
								size="paragraph-3"
								weight="regular"
								dangerouslySetClassName={`py-line-clamp-1 ${isExpanded ? "text-primary-5" : ""}`}
							>
								{topResponseText}
							</Text>
						</span>
					) : null}
				</div>
			</div>
			{percentage && <Chip size={24} label={`${percentage.toFixed(0)}%`} color="primary" />}
		</div>
	);
};
